import { Pause, Play, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface VoiceClip {
  id: string;
  blob: Blob;
  durationMs: number;
}

interface VoiceClipListProps {
  clips: VoiceClip[];
  onDelete: (clipId: string) => void;
}

export function VoiceClipList({ clips, onDelete }: VoiceClipListProps) {
  const { t } = useTranslation();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const formatDuration = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const playClip = (clip: VoiceClip) => {
    if (audioRef.current) audioRef.current.pause();
    const url = URL.createObjectURL(clip.blob);
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setPlayingId(null);
    audio.play();
    setPlayingId(clip.id);
  };

  const stopClip = () => {
    audioRef.current?.pause();
    setPlayingId(null);
  };

  if (clips.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {t('Feedback.clipList.title', { count: clips.length })}
      </p>

      {clips.map((clip, i) => (
        <div
          key={clip.id}
          className="flex items-center gap-2 rounded-lg border border-border bg-background p-3"
        >
          <span className="text-xs text-muted-foreground w-6 shrink-0">{i + 1}.</span>

          <span className="text-sm text-muted-foreground shrink-0">
            {formatDuration(clip.durationMs)}
          </span>

          <div className="flex items-center gap-1 ml-auto">
            <button
              type="button"
              onClick={() => (playingId === clip.id ? stopClip() : playClip(clip))}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
              aria-label={
                playingId === clip.id ? t('Feedback.clipList.pause') : t('Feedback.clipList.play')
              }
            >
              {playingId === clip.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => onDelete(clip.id)}
              className="flex h-8 w-8 items-center justify-center rounded-full text-destructive hover:bg-destructive/10"
              aria-label={t('Feedback.clipList.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
